import { GradientParser } from '../../lib/gradient';
import { TYPES } from '../constants';
import { renderBevelBubble } from '../textfx/bevel';
import { renderEmboss } from '../textfx/emboss';
import { renderInnerGlow } from '../textfx/inner-glow';
import { renderRaylight } from '../textfx/raylight';
import { resolveRadius } from './radius';

// Apply independent WebGL text effects (not dynamic-shader).
// Usage: node.textFx = { type: 'bevel' | 'bubble', params: { ... } }
// This wrapper renders the text into an offscreen 2D canvas, runs WebGL pass, then composites to main.

export function renderTextWithTextFx(renderContext, node, render) {
  if (node.type !== TYPES.text) return render(renderContext, node);
  let fx = node.textFx;
  if (!fx) return render(renderContext, node);
  if (typeof fx === 'string') {
    fx = parseTextFxString(fx);
  }
  if (!fx || !fx.type) return render(renderContext, node);

  const { context } = renderContext;

  // Offscreen draw of text with padding to accommodate glow/bevel spread
  const padding = Math.max(16, Math.floor((node.fontSize || 16) * 0.6));

  // HiDPI + Zoom render to avoid blur when scaling
  const dpr = renderContext.dpr || 1;
  const scaleProvider = renderContext.providers?.scale;
  const zoom = Math.max(1, (scaleProvider?.scale || 1) * Math.max(2, dpr));

  const offscreen = document.createElement('canvas');
  offscreen.width = Math.max(1, Math.floor((node.w + padding * 2) * zoom));
  offscreen.height = Math.max(1, Math.floor((node.h + padding * 2) * zoom));
  const offctx = offscreen.getContext('2d');
  offctx.scale(zoom, zoom);

  const tempRenderContext = { ...renderContext, context: offctx };
  // Render text only in offscreen: strip background/border to avoid affecting bevel normals
  const tempNode = { ...node, x: padding, y: padding, background: null, borderWidth: 0 };

  // Render base text (prevent nested text-fx)
  tempRenderContext.__textFxActive = true;
  render(tempRenderContext, tempNode);
  delete tempRenderContext.__textFxActive;

  // Dispatch to effect
  let effected;
  const gl = getSharedWebGLContext(offscreen.width, offscreen.height);
  switch (fx.type) {
    case 'bevel':
    case 'bubble': {
      effected = renderBevelBubble(offscreen, fx.params || {}, gl);
      break;
    }
    case 'inner-glow': {
      effected = renderInnerGlow(offscreen, fx.params || {}, gl);
      break;
    }
    case 'emboss': {
      effected = renderEmboss(offscreen, fx.params || {}, gl);
      break;
    }
    case 'raylight': {
      effected = renderRaylight(offscreen, fx.params || {}, gl);
      break;
    }
    default: {
      // Unknown effect: just draw base
      effected = offscreen;
    }
  }

  // Draw background (on main canvas) if present to preserve original look
  if (node.background) {
    _drawBackground(context, node);
  }

  // Composite to main canvas
  const prevSmooth = context.imageSmoothingEnabled;
  const prevQuality = context.imageSmoothingQuality;
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = renderContext.providers?.scale?.scaling
    ? 'low'
    : renderContext.highQuality
      ? 'high'
      : 'medium';
  context.drawImage(
    effected,
    0,
    0,
    effected.width,
    effected.height,
    node.x - padding,
    node.y - padding,
    node.w + padding * 2,
    node.h + padding * 2,
  );
  context.imageSmoothingEnabled = prevSmooth;
  if (prevQuality) context.imageSmoothingQuality = prevQuality;

  // Draw border if specified (on main canvas)
  if (node.borderWidth) {
    _drawBorder(context, node);
  }
}

// Shared WebGL context to avoid exceeding context limits
let __sharedGLCanvas = null;
let __sharedGL = null;
export function getSharedWebGLContext(width, height) {
  if (!__sharedGLCanvas) {
    __sharedGLCanvas = document.createElement('canvas');
    __sharedGL =
      __sharedGLCanvas.getContext('webgl', { premultipliedAlpha: true }) ||
      __sharedGLCanvas.getContext('experimental-webgl');
  }
  if (__sharedGL && __sharedGLCanvas) {
    if (__sharedGLCanvas.width !== width) __sharedGLCanvas.width = width;
    if (__sharedGLCanvas.height !== height) __sharedGLCanvas.height = height;
  }
  return __sharedGL;
}

// Draw a single glyph with textFx at character level
// Supports solid color and gradient fills. For complex shader fills, fall back to normal draw upstream.
export function drawGlyphWithTextFx(
  lineContext,
  targetCtx,
  char,
  x,
  y,
  candidateFill,
  measureFont,
  fx,
) {
  const { renderContext } = lineContext;
  if (typeof fx === 'string') fx = parseTextFxString(fx);
  const dpr = renderContext.dpr || 1;
  const scaleProvider = renderContext.providers?.scale;
  const zoom = Math.max(1, (scaleProvider?.scale || 1) * Math.max(2, dpr));

  const w = Math.max(
    1,
    Math.floor(
      lineContext
        ? lineContext.fontValue
          ? targetCtx.measureText
            ? targetCtx.measureText(char).width || 8
            : 12
          : 12
        : 12,
    ),
  );
  const charW = Math.max(1, Math.ceil(w));
  const charH = Math.max(1, Math.ceil(lineContext.h));
  const pad = Math.max(8, Math.floor(charH * 0.5));

  const off = document.createElement('canvas');
  off.width = Math.max(1, Math.floor((charW + pad * 2) * zoom));
  off.height = Math.max(1, Math.floor((charH + pad * 2) * zoom));
  const offctx = off.getContext('2d');
  offctx.scale(zoom, zoom);
  offctx.font = measureFont || lineContext.fontValue;
  offctx.textBaseline = 'middle';
  offctx.fillStyle = '#000';

  const rect = { x: pad, y: pad, w: charW, h: charH };
  if (candidateFill && GradientParser.is(candidateFill)) {
    GradientParser.create(offctx, candidateFill, rect, () => {
      offctx.fillText(char, rect.x, rect.y + Math.floor(rect.h / 2));
    });
  } else {
    offctx.fillStyle = candidateFill || '#111';
    offctx.fillText(char, rect.x, rect.y + Math.floor(rect.h / 2));
  }

  const gl = getSharedWebGLContext(off.width, off.height);
  let effected;
  switch ((fx?.type || '').toLowerCase()) {
    case 'bevel':
    case 'bubble':
      effected = renderBevelBubble(off, fx?.params || {}, gl);
      break;
    case 'inner-glow':
      effected = renderInnerGlow(off, fx?.params || {}, gl);
      break;
    case 'emboss':
      effected = renderEmboss(off, fx?.params || {}, gl);
      break;
    case 'raylight':
      effected = renderRaylight(off, fx?.params || {}, gl);
      break;
    default:
      effected = off;
      break;
  }

  const prevSmooth = targetCtx.imageSmoothingEnabled;
  const prevQuality = targetCtx.imageSmoothingQuality;
  targetCtx.imageSmoothingEnabled = true;
  targetCtx.imageSmoothingQuality = renderContext.providers?.scale?.scaling
    ? 'low'
    : renderContext.highQuality
      ? 'high'
      : 'medium';
  targetCtx.drawImage(
    effected,
    0,
    0,
    effected.width,
    effected.height,
    x - pad,
    y - Math.floor(rect.h / 2) - pad,
    rect.w + pad * 2,
    rect.h + pad * 2,
  );
  targetCtx.imageSmoothingEnabled = prevSmooth;
  if (prevQuality) targetCtx.imageSmoothingQuality = prevQuality;
}

// text-fx parser: text-fx(Name, key=value; key=value)
export function parseTextFxString(value) {
  const m = String(value || '').match(/text-fx\s*\(\s*([^,\s)]+)\s*(?:,\s*(.*))?\)\s*$/i);
  if (!m) return null;
  const name = m[1].trim();
  const propsStr = (m[2] || '').trim();
  const params = {};
  if (propsStr) {
    const items = propsStr.split(/\s*;\s*/).filter(Boolean);
    for (const it of items) {
      const kv = it.split(/\s*=\s*/);
      if (kv.length === 2) {
        const k = kv[0];
        const raw = kv[1];
        const v =
          raw === 'true'
            ? true
            : raw === 'false'
              ? false
              : isFinite(Number(raw))
                ? Number(raw)
                : raw;
        params[k] = v;
      }
    }
  }
  return { type: name.toLowerCase(), params };
}

function _drawBackground(ctx, node) {
  const x = node.x;
  const y = node.y;
  const w = node.w;
  const h = node.h;
  ctx.beginPath();
  if (node.radius) {
    const { topLeft, topRight, bottomRight, bottomLeft } = resolveRadius(w, h, node.radius);
    ctx.moveTo(x + topLeft, y);
    ctx.lineTo(x + w - topRight, y);
    ctx.arcTo(x + w, y, x + w, y + topRight, topRight);
    ctx.lineTo(x + w, y + h - bottomRight);
    ctx.arcTo(x + w, y + h, x + w - bottomRight, y + h, bottomRight);
    ctx.lineTo(x + bottomLeft, y + h);
    ctx.arcTo(x, y + h, x, y + h - bottomLeft, bottomLeft);
    ctx.lineTo(x, y + topLeft);
    ctx.arcTo(x, y, x + topLeft, y, topLeft);
  } else {
    ctx.rect(x, y, w, h);
  }
  ctx.closePath();

  const bg = node.background;
  if (GradientParser.is(bg)) {
    GradientParser.create(ctx, bg, { x, y, w, h }, () => {
      ctx.fill();
    });
  } else {
    ctx.fillStyle = bg;
    ctx.fill();
  }
}

function _drawBorder(ctx, node) {
  const x = node.x;
  const y = node.y;
  const w = node.w;
  const h = node.h;
  const bw = node.borderWidth || 1;
  ctx.beginPath();
  if (node.radius) {
    const { topLeft, topRight, bottomRight, bottomLeft } = resolveRadius(w, h, node.radius);
    ctx.moveTo(x + topLeft, y);
    ctx.lineTo(x + w - topRight, y);
    ctx.arcTo(x + w, y, x + w, y + topRight, topRight);
    ctx.lineTo(x + w, y + h - bottomRight);
    ctx.arcTo(x + w, y + h, x + w - bottomRight, y + h, bottomRight);
    ctx.lineTo(x + bottomLeft, y + h);
    ctx.arcTo(x, y + h, x, y + h - bottomLeft, bottomLeft);
    ctx.lineTo(x, y + topLeft);
    ctx.arcTo(x, y, x + topLeft, y, topLeft);
  } else {
    ctx.rect(x, y, w, h);
  }
  ctx.closePath();
  ctx.lineWidth = bw;
  ctx.strokeStyle = node.borderColor || 'rgba(0,0,0,0.2)';
  ctx.stroke();
}
